export type ListRule = {
  name: string;
  regex: RegExp;
  getPrefix: (
    line: string,
    match: RegExpMatchArray,
  ) => { prefix: string; matched: string; rest: string };
};

export const listRules: ListRule[] = [
  {
    name: "task",
    regex: /^([ \t]*)([-*+])\s\[[ xX]?\]\s+/,
    getPrefix: (line, match) => {
      const matched = match[0];
      return { prefix: match[1] + "- [ ] ", matched, rest: line.slice(matched.length) };
    },
  },
  {
    name: "unordered",
    regex: /^([ \t]*)([-*+])\s+/,
    getPrefix: (line, match) => {
      const matched = match[0];
      return { prefix: match[1] + match[2] + " ", matched, rest: line.slice(matched.length) };
    },
  },
  {
    name: "ordered",
    regex: /^([ \t]*)(\d+)\.\s+/,
    getPrefix: (line, match) => {
      const matched = match[0];
      const num = parseInt(match[2], 10) + 1;
      return { prefix: match[1] + num + ". ", matched, rest: line.slice(matched.length) };
    },
  },
];

export class MarkdownEditorCore {
  content: string;
  cursor: {
    start: number;
    end: number;
  };

  constructor(content = "") {
    this.content = content;
    this.cursor = { start: content.length ?? 0, end: content.length };
  }

  private insertContent = (value: string, start: number, end: number = start) => {
    const before = this.content.slice(0, start);
    const after = this.content.slice(end, this.content.length);
    this.content = before + value + after;
    return this;
  };

  private getTextChunk = (content: string, start: number, end: number = start) => {
    while (start > 0 && !/\s/.test(content[start - 1])) start--;
    while (end < content.length && !/\s/.test(content[end])) end++;

    return {
      chunk: content.slice(start, end),
      start,
      end,
    };
  };

  private getLineChunks = (content: string, start: number, end: number = start) => {
    let lineStart = start;
    let lineEnd = end;

    while (lineStart > 0 && content[lineStart - 1] !== "\n") lineStart--;
    while (lineEnd < content.length && content[lineEnd] !== "\n") lineEnd++;

    const lines = content.slice(lineStart, lineEnd).split("\n");
    let offset = lineStart;
    const chunks = lines.map((chunk) => {
      const start = offset;
      const end = offset + chunk.length;
      offset = end + 1;
      return { chunk, start, end };
    });
    return chunks;
  };

  private adjustMarkers = (value: string, prefix: string, suffix: string = prefix) => {
    const isWrapped = value.startsWith(prefix) && value.endsWith(suffix);
    return {
      text: isWrapped
        ? value.slice(prefix.length, value.length - suffix.length)
        : prefix + value + suffix,
      cursorOffset: isWrapped
        ? { start: -prefix.length, end: -suffix.length }
        : { start: prefix.length, end: suffix.length },
    };
  };

  private heading = (pattern: string) => {
    const lines = this.getLineChunks(this.content, this.cursor.start);
    const { text, cursorOffset } = this.adjustMarkers(lines[0].chunk, pattern, "");
    this.insertContent(text, lines[0].start, lines[0].end);
    this.setCursor(this.cursor.start + cursorOffset.start, this.cursor.end + cursorOffset.start);
    return this;
  };

  private style = (prefix: string, suffix: string = prefix) => {
    const { start: cursorStart, end: cursorEnd } = this.cursor;
    const isContentSelected = cursorStart !== cursorEnd;

    // cursor only
    if (!isContentSelected) {
      const { chunk, start, end } = this.getTextChunk(this.content, cursorStart);
      if (start < cursorStart && cursorStart < end) {
        const { text, cursorOffset } = this.adjustMarkers(chunk, prefix, suffix);
        this.insertContent(text, start, end);
        this.setCursor(cursorStart + cursorOffset.start);
        return this;
      }
      this.insertContent(prefix + suffix, cursorStart);
      this.setCursor(cursorStart + prefix.length);
      return this;
    }

    // selection
    const { chunk, start, end } = this.getTextChunk(this.content, cursorStart, cursorEnd);
    const { text, cursorOffset } = this.adjustMarkers(chunk.trim(), prefix, suffix);
    this.insertContent(text, start, end);
    this.setCursor(cursorStart + cursorOffset.start, cursorEnd + cursorOffset.end);
    return this;
  };

  private list = (pattern: string, type: "unordered" | "ordered" | "task" = "unordered") => {
    const { start: cursorStart, end: cursorEnd } = this.cursor;
    const isContentSelected = cursorStart !== cursorEnd;

    // --- Cursor only ---
    if (!isContentSelected) {
      const chunks = this.getLineChunks(this.content, cursorStart);
      const marker = type === "ordered" ? "1. " : pattern;
      const { cursorOffset, text } = this.adjustMarkers(chunks[0].chunk, marker, "");
      this.insertContent(text, chunks[0].start, chunks[0].end);
      this.setCursor(cursorStart + cursorOffset.start);
      return this;
    }

    // --- Multiple lines selected ---
    const chunks = this.getLineChunks(this.content, cursorStart, cursorEnd);
    let adjustedStart = cursorStart;
    let totalOffset = 0;

    chunks.forEach(({ chunk, start, end }, index) => {
      const marker = type === "ordered" ? `${index + 1}. ` : pattern;
      const { text, cursorOffset } = this.adjustMarkers(chunk, marker, "");
      if (index === 0) adjustedStart += cursorOffset.start;

      const newStart = start + totalOffset;
      const newEnd = end + totalOffset;

      this.insertContent(text, newStart, newEnd);
      totalOffset += cursorOffset.start + cursorOffset.end;
    });

    this.setCursor(adjustedStart, cursorEnd + totalOffset);
    return this;
  };

  h1 = () => this.heading("# ");
  h2 = () => this.heading("## ");
  h3 = () => this.heading("### ");

  bold = () => this.style("**");
  italic = () => this.style("_");

  unordered = () => this.list("- ");
  ordered = () => this.list("1. ", "ordered");
  task = () => this.list("- [ ] ");

  quote = () => this.list("> ");
  code = () => this.style("`");
  link = () => this.style("[", "](url)");

  getContent = () => this.content;
  getCursor = () => this.cursor;

  setContent = (content: string) => {
    this.content = content;
    return this;
  };

  setCursor = (start: number, end = start) => {
    this.cursor = {
      start,
      end,
    };
    return this;
  };

  handleEnter = () => {
    const start = this.cursor.start;
    const before = this.content.slice(0, start);
    const lineStart = before.lastIndexOf("\n") + 1;
    const currentLine = before.slice(lineStart);

    for (const rule of listRules) {
      const match = currentLine.match(rule.regex);
      if (!match) continue;

      const { prefix, rest } = rule.getPrefix(currentLine, match);
      if (rest) {
        this.content = before + "\n" + prefix + this.content.slice(start);
        this.setCursor(start + 1 + prefix.length);
        return this;
      }

      // If line is empty → remove the prefix
      this.content = before.slice(0, lineStart) + this.content.slice(start);
      this.setCursor(lineStart);
      return this;
    }

    // Default: insert double newline for paragraph break
    this.content = before + "\n" + this.content.slice(start);
    this.setCursor(start + 1);
    return this;
  };
}
